import React, { useEffect, useState } from 'react';
import {
  getCompanies,
  getInterCompanyTransactions,
  createInterCompanyTransaction,
  approveInterCompanyTransaction,
  getInterCompanyBalances,
  getBudgets,
  createBudget,
  approveBudget,
  getBudgetVsActual,
  getFinancialForecast,
  exportProfitLoss,
  exportBalanceSheet,
  exportTrends,
  getAuditTrail,
  getPendingApprovals,
  getChartOfAccounts
} from '../lib/api';
import {
  LineChart as RechartsLine,
  Line,
  AreaChart,
  Area,
  BarChart as RechartsBar,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Building2,
  ArrowLeftRight,
  Target,
  TrendingUp,
  TrendingDown,
  FileSpreadsheet,
  FileText,
  Shield,
  Clock,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Plus,
  Download,
  History,
  DollarSign,
  Percent,
  BarChart3,
  LineChart
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (value, decimals = 0) => {
  if (value === undefined || value === null) return '0';
  return value.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

export const GovernancePage = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [activeTab, setActiveTab] = useState('inter-company');
  const [loading, setLoading] = useState(false);

  // Inter-Company
  const [icTransactions, setIcTransactions] = useState([]);
  const [icBalances, setIcBalances] = useState(null);
  const [icDialogOpen, setIcDialogOpen] = useState(false);
  const [newIcTransaction, setNewIcTransaction] = useState({
    from_company_id: '',
    to_company_id: '',
    amount: 0,
    transaction_type: 'service',
    description: '',
    reference: ''
  });

  // Budgets
  const [budgets, setBudgets] = useState([]);
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [newBudget, setNewBudget] = useState({
    fiscal_year: new Date().getFullYear().toString(),
    account_id: '',
    budget_type: 'expense',
    amounts: {},
    notes: ''
  });

  // Budget vs Actual
  const [budgetVsActual, setBudgetVsActual] = useState(null);

  // Forecast
  const [forecast, setForecast] = useState(null);

  // Audit Trail
  const [auditTrail, setAuditTrail] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState(null);

  // Export
  const [exportPeriodFrom, setExportPeriodFrom] = useState(`${new Date().getFullYear()}-01`);
  const [exportPeriodTo, setExportPeriodTo] = useState(new Date().toISOString().slice(0, 7));
  const [exportAsOfDate, setExportAsOfDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadIcTransactions();
      loadBudgets();
      loadAccounts();
      loadPendingApprovals();
    }
  }, [selectedCompany]);

  const loadCompanies = async () => {
    try {
      const response = await getCompanies();
      setCompanies(response.data);
      if (response.data.length > 0) {
        setSelectedCompany(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const loadIcTransactions = async () => {
    try {
      const [txnRes, balRes] = await Promise.all([
        getInterCompanyTransactions(selectedCompany),
        getInterCompanyBalances()
      ]);
      setIcTransactions(txnRes.data);
      setIcBalances(balRes.data);
    } catch (error) {
      console.error('Failed to load IC data:', error);
    }
  };

  const loadBudgets = async () => {
    try {
      const response = await getBudgets(selectedCompany);
      setBudgets(response.data);
    } catch (error) {
      console.error('Failed to load budgets:', error);
    }
  };

  const loadAccounts = async () => {
    try {
      const response = await getChartOfAccounts(selectedCompany);
      setAccounts(response.data);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    }
  };

  const loadBudgetVsActual = async () => {
    setLoading(true);
    try {
      const response = await getBudgetVsActual(selectedCompany, new Date().getFullYear().toString());
      setBudgetVsActual(response.data);
    } catch (error) {
      toast.error('Failed to load budget vs actual');
    } finally {
      setLoading(false);
    }
  };

  const loadForecast = async () => {
    setLoading(true);
    try {
      const response = await getFinancialForecast(selectedCompany, 6);
      setForecast(response.data);
    } catch (error) {
      toast.error('Failed to load forecast');
    } finally {
      setLoading(false);
    }
  };

  const loadAuditTrail = async () => {
    try {
      const response = await getAuditTrail({ limit: 50 });
      setAuditTrail(response.data.entries || []);
    } catch (error) {
      console.error('Failed to load audit trail:', error);
    }
  };

  const loadPendingApprovals = async () => {
    try {
      const response = await getPendingApprovals(selectedCompany);
      setPendingApprovals(response.data);
    } catch (error) {
      console.error('Failed to load pending approvals:', error);
    }
  };

  const handleCreateIcTransaction = async () => {
    try {
      await createInterCompanyTransaction(newIcTransaction);
      toast.success('IC transaction created');
      setIcDialogOpen(false);
      loadIcTransactions();
      setNewIcTransaction({
        from_company_id: '',
        to_company_id: '',
        amount: 0,
        transaction_type: 'service',
        description: '',
        reference: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create IC transaction');
    }
  };

  const handleApproveIc = async (icId) => {
    try {
      await approveInterCompanyTransaction(icId);
      toast.success('IC transaction approved and journal entries created');
      loadIcTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve IC transaction');
    }
  };

  const handleCreateBudget = async () => {
    try {
      await createBudget({
        company_id: selectedCompany,
        ...newBudget
      });
      toast.success('Budget created');
      setBudgetDialogOpen(false);
      loadBudgets();
      setNewBudget({
        fiscal_year: new Date().getFullYear().toString(),
        account_id: '',
        budget_type: 'expense',
        amounts: {},
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create budget');
    }
  };

  const handleApproveBudget = async (budgetId) => {
    try {
      await approveBudget(budgetId);
      toast.success('Budget approved');
      loadBudgets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve budget');
    }
  };

  const handleExport = async (type, format) => {
    try {
      let response;
      let filename;
      
      if (type === 'pnl') {
        response = await exportProfitLoss(selectedCompany, exportPeriodFrom, exportPeriodTo, format);
        filename = `PnL_${exportPeriodFrom}_${exportPeriodTo}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      } else if (type === 'bs') {
        response = await exportBalanceSheet(selectedCompany, exportAsOfDate, format);
        filename = `BalanceSheet_${exportAsOfDate}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      } else if (type === 'trends') {
        response = await exportTrends(selectedCompany, 12, 'excel');
        filename = `Trends_12months.xlsx`;
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${type.toUpperCase()} exported successfully`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const updateBudgetMonth = (month, value) => {
    setNewBudget({
      ...newBudget,
      amounts: {
        ...newBudget.amounts,
        [month]: parseFloat(value) || 0
      }
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight" data-testid="governance-title">
            Governance & Budgeting
          </h1>
          <p className="text-muted-foreground mt-1">
            Inter-Company, Budgets, Forecasting & Export
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedCompany} onValueChange={setSelectedCompany}>
            <SelectTrigger className="w-[200px]">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Select Company" />
            </SelectTrigger>
            <SelectContent>
              {companies.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Pending Approvals Banner */}
      {pendingApprovals && pendingApprovals.total_pending > 0 && (
        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <span className="font-semibold text-amber-800">
              {pendingApprovals.total_pending} items pending approval
            </span>
            <div className="flex gap-2 ml-auto">
              {pendingApprovals.pending.journal_entries?.length > 0 && (
                <Badge variant="outline">{pendingApprovals.pending.journal_entries.length} JE</Badge>
              )}
              {pendingApprovals.pending.budgets?.length > 0 && (
                <Badge variant="outline">{pendingApprovals.pending.budgets.length} Budgets</Badge>
              )}
              {pendingApprovals.pending.ic_transactions?.length > 0 && (
                <Badge variant="outline">{pendingApprovals.pending.ic_transactions.length} IC</Badge>
              )}
            </div>
          </div>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="inter-company" data-testid="ic-tab">Inter-Company</TabsTrigger>
          <TabsTrigger value="budgets" data-testid="budgets-tab">Budgets</TabsTrigger>
          <TabsTrigger value="variance" data-testid="variance-tab">Budget vs Actual</TabsTrigger>
          <TabsTrigger value="forecast" data-testid="forecast-tab">Forecast</TabsTrigger>
          <TabsTrigger value="export" data-testid="export-tab">Export</TabsTrigger>
          <TabsTrigger value="audit" data-testid="audit-tab">Audit Trail</TabsTrigger>
        </TabsList>

        {/* Inter-Company Tab */}
        <TabsContent value="inter-company" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <ArrowLeftRight className="h-6 w-6" />
              Inter-Company Transactions
            </h2>
            <div className="flex gap-2">
              <Button variant="outline" onClick={loadIcTransactions}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={() => setIcDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New IC Transaction
              </Button>
            </div>
          </div>

          {/* IC Balances Summary */}
          {icBalances && (
            <div className="grid grid-cols-3 gap-4">
              <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <p className="text-sm text-blue-700">Total IC Receivables</p>
                <p className="text-2xl font-bold text-blue-800">{formatCurrency(icBalances.total_ic_receivables)}</p>
              </Card>
              <Card className="p-4 bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                <p className="text-sm text-red-700">Total IC Payables</p>
                <p className="text-2xl font-bold text-red-800">{formatCurrency(icBalances.total_ic_payables)}</p>
              </Card>
              <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                <p className="text-sm text-purple-700">Elimination Required</p>
                <p className="text-2xl font-bold text-purple-800">{formatCurrency(icBalances.elimination_required)}</p>
              </Card>
            </div>
          )}

          {/* IC Transactions List */}
          <Card className="p-4">
            <h3 className="font-semibold mb-4">Transactions</h3>
            {icTransactions.length > 0 ? (
              <div className="space-y-3">
                {icTransactions.map(txn => (
                  <div key={txn.id} className="p-4 border rounded-lg hover:bg-slate-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{txn.from_company_name}</span>
                          <ArrowLeftRight className="h-4 w-4 text-muted-foreground" />
                          <span className="font-semibold">{txn.to_company_name}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{txn.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold">{formatCurrency(txn.amount)} SAR</p>
                        <Badge className={
                          txn.status === 'approved' ? 'bg-green-100 text-green-800' :
                          txn.status === 'eliminated' ? 'bg-purple-100 text-purple-800' :
                          'bg-amber-100 text-amber-800'
                        }>
                          {txn.status}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-3 pt-3 border-t">
                      <span className="text-sm text-muted-foreground">
                        {txn.transaction_type} • {txn.transaction_date}
                      </span>
                      {txn.status === 'pending' && (
                        <Button size="sm" onClick={() => handleApproveIc(txn.id)}>
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Approve
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <ArrowLeftRight className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No inter-company transactions yet</p>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Budgets Tab */}
        <TabsContent value="budgets" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Target className="h-6 w-6" />
              Budget Management
            </h2>
            <div className="flex gap-2">
              <Button variant="outline" onClick={loadBudgets}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={() => setBudgetDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Budget
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Card className="p-4">
              <p className="text-sm text-muted-foreground">Total Budgets</p>
              <p className="text-2xl font-bold">{budgets.length}</p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-muted-foreground">Approved</p>
              <p className="text-2xl font-bold text-green-600">
                {budgets.filter(b => b.status === 'approved').length}
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-muted-foreground">Pending Approval</p>
              <p className="text-2xl font-bold text-amber-600">
                {budgets.filter(b => b.status === 'draft').length}
              </p>
            </Card>
          </div>

          <Card className="p-4">
            <h3 className="font-semibold mb-4">Budget List</h3>
            {budgets.length > 0 ? (
              <div className="space-y-3">
                {budgets.map(budget => (
                  <div key={budget.id} className="p-4 border rounded-lg hover:bg-slate-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold">{budget.account_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {budget.account_code} • {budget.fiscal_year} • {budget.budget_type}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold">{formatCurrency(budget.total_budget)} SAR</p>
                        <Badge className={
                          budget.status === 'approved' ? 'bg-green-100 text-green-800' :
                          budget.status === 'locked' ? 'bg-blue-100 text-blue-800' :
                          'bg-amber-100 text-amber-800'
                        }>
                          {budget.status}
                        </Badge>
                      </div>
                    </div>
                    {budget.status === 'draft' && (
                      <div className="mt-3 pt-3 border-t flex justify-end">
                        <Button size="sm" onClick={() => handleApproveBudget(budget.id)}>
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Approve
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Target className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No budgets created yet</p>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Budget vs Actual Tab */}
        <TabsContent value="variance" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <BarChart3 className="h-6 w-6" />
              Budget vs Actual Analysis
            </h2>
            <Button onClick={loadBudgetVsActual} disabled={loading}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Load Analysis
            </Button>
          </div>

          {budgetVsActual ? (
            <>
              <div className="grid grid-cols-4 gap-4">
                <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <p className="text-sm text-blue-700">Total Budgeted</p>
                  <p className="text-2xl font-bold text-blue-800">{formatCurrency(budgetVsActual.summary?.total_budgeted)}</p>
                </Card>
                <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <p className="text-sm text-green-700">Total Actual</p>
                  <p className="text-2xl font-bold text-green-800">{formatCurrency(budgetVsActual.summary?.total_actual)}</p>
                </Card>
                <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <p className="text-sm text-purple-700">Total Variance</p>
                  <p className={`text-2xl font-bold ${budgetVsActual.summary?.total_variance >= 0 ? 'text-green-800' : 'text-red-800'}`}>
                    {formatCurrency(budgetVsActual.summary?.total_variance)}
                  </p>
                </Card>
                <Card className={`p-4 ${budgetVsActual.summary?.overall_status === 'under_budget' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <p className={`text-xl font-bold ${budgetVsActual.summary?.overall_status === 'under_budget' ? 'text-green-800' : 'text-red-800'}`}>
                    {budgetVsActual.summary?.overall_status === 'under_budget' ? 'Under Budget' : 'Over Budget'}
                  </p>
                </Card>
              </div>

              <Card className="p-4">
                <h3 className="font-semibold mb-4">Variance Details</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left py-2 px-3">Account</th>
                        <th className="text-right py-2 px-3">Budgeted</th>
                        <th className="text-right py-2 px-3">Actual</th>
                        <th className="text-right py-2 px-3">Variance</th>
                        <th className="text-right py-2 px-3">Variance %</th>
                        <th className="py-2 px-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {budgetVsActual.details?.map((item, idx) => (
                        <tr key={idx} className="border-b hover:bg-slate-50">
                          <td className="py-2 px-3">
                            <p className="font-medium">{item.account_name}</p>
                            <p className="text-xs text-muted-foreground">{item.account_code}</p>
                          </td>
                          <td className="py-2 px-3 text-right font-mono">{formatCurrency(item.budgeted)}</td>
                          <td className="py-2 px-3 text-right font-mono">{formatCurrency(item.actual)}</td>
                          <td className={`py-2 px-3 text-right font-mono ${item.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(item.variance)}
                          </td>
                          <td className={`py-2 px-3 text-right font-mono ${item.variance_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {item.variance_pct}%
                          </td>
                          <td className="py-2 px-3 text-center">
                            {item.status === 'under_budget' ? (
                              <TrendingDown className="h-4 w-4 text-green-500 inline" />
                            ) : (
                              <TrendingUp className="h-4 w-4 text-red-500 inline" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          ) : (
            <Card className="p-12 text-center">
              <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Load Budget vs Actual</h3>
              <p className="text-muted-foreground">Click the button above to load variance analysis</p>
            </Card>
          )}
        </TabsContent>

        {/* Forecast Tab */}
        <TabsContent value="forecast" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <LineChart className="h-6 w-6" />
              Financial Forecast
            </h2>
            <Button onClick={loadForecast} disabled={loading}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Generate Forecast
            </Button>
          </div>

          {forecast ? (
            <>
              {/* Assumptions */}
              <Card className="p-4 bg-blue-50 border-blue-200">
                <h3 className="font-semibold mb-2 text-blue-800">Forecast Assumptions</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-blue-700">Revenue Growth Rate</p>
                    <p className="font-bold text-blue-900">{forecast.assumptions?.revenue_growth_rate}% per month</p>
                  </div>
                  <div>
                    <p className="text-blue-700">Cost Growth Rate</p>
                    <p className="font-bold text-blue-900">{forecast.assumptions?.cost_growth_rate}% per month</p>
                  </div>
                  <div>
                    <p className="text-blue-700">Method</p>
                    <p className="font-bold text-blue-900">Linear Projection</p>
                  </div>
                </div>
              </Card>

              {/* Forecast Chart */}
              <Card className="p-4">
                <h3 className="font-semibold mb-4">Historical vs Projected Revenue</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={[
                    ...forecast.historical.map(h => ({ ...h, type: 'historical', projected_revenue: null })),
                    ...forecast.forecast.map(f => ({ period: f.period, revenue: null, type: 'forecast', projected_revenue: f.projected_revenue }))
                  ]}>
                    <defs>
                      <linearGradient id="colorHistorical" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorProjected" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                    <Tooltip 
                      formatter={(value) => value ? formatCurrency(value) : 'N/A'}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    />
                    <Legend />
                    <ReferenceLine x={forecast.historical[forecast.historical.length - 1]?.period} stroke="#666" strokeDasharray="5 5" label="Today" />
                    <Area type="monotone" dataKey="revenue" name="Historical" stroke="#22c55e" fillOpacity={1} fill="url(#colorHistorical)" connectNulls={false} />
                    <Area type="monotone" dataKey="projected_revenue" name="Projected" stroke="#8b5cf6" fillOpacity={1} fill="url(#colorProjected)" strokeDasharray="5 5" connectNulls={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>

              <div className="grid grid-cols-2 gap-4">
                {/* Historical */}
                <Card className="p-4">
                  <h3 className="font-semibold mb-4">Historical Data (Last 6 Months)</h3>
                  <div className="space-y-2">
                    {forecast.historical?.map((h, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b">
                        <span className="font-medium">{h.period}</span>
                        <div className="text-right">
                          <span className="text-green-600 mr-4">Rev: {formatCurrency(h.revenue)}</span>
                          <span className="text-red-600">Cost: {formatCurrency(h.costs)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Forecast */}
                <Card className="p-4">
                  <h3 className="font-semibold mb-4">Projected (Next 6 Months)</h3>
                  <div className="space-y-2">
                    {forecast.forecast?.map((f, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b">
                        <div>
                          <span className="font-medium">{f.period}</span>
                          <Badge className={`ml-2 text-xs ${
                            f.confidence === 'high' ? 'bg-green-100' :
                            f.confidence === 'medium' ? 'bg-amber-100' : 'bg-red-100'
                          }`}>
                            {f.confidence}
                          </Badge>
                        </div>
                        <div className="text-right">
                          <span className="text-green-600 mr-4">Rev: {formatCurrency(f.projected_revenue)}</span>
                          <span className={f.projected_margin >= 0 ? 'text-blue-600' : 'text-red-600'}>
                            Margin: {formatCurrency(f.projected_margin)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            </>
          ) : (
            <Card className="p-12 text-center">
              <LineChart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Generate Financial Forecast</h3>
              <p className="text-muted-foreground">Click the button above to generate 6-month forecast</p>
            </Card>
          )}
        </TabsContent>

        {/* Export Tab */}
        <TabsContent value="export" className="space-y-4">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Download className="h-6 w-6" />
            Export Financial Reports
          </h2>

          <div className="grid grid-cols-3 gap-6">
            {/* P&L Export */}
            <Card className="p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5" />
                Profit & Loss
              </h3>
              <div className="space-y-3">
                <div>
                  <Label>Period From</Label>
                  <Input type="month" value={exportPeriodFrom} onChange={(e) => setExportPeriodFrom(e.target.value)} />
                </div>
                <div>
                  <Label>Period To</Label>
                  <Input type="month" value={exportPeriodTo} onChange={(e) => setExportPeriodTo(e.target.value)} />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button className="flex-1" onClick={() => handleExport('pnl', 'excel')}>
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Excel
                  </Button>
                  <Button className="flex-1" variant="outline" onClick={() => handleExport('pnl', 'pdf')}>
                    <FileText className="h-4 w-4 mr-2" />
                    PDF
                  </Button>
                </div>
              </div>
            </Card>

            {/* Balance Sheet Export */}
            <Card className="p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5" />
                Balance Sheet
              </h3>
              <div className="space-y-3">
                <div>
                  <Label>As of Date</Label>
                  <Input type="date" value={exportAsOfDate} onChange={(e) => setExportAsOfDate(e.target.value)} />
                </div>
                <div className="flex gap-2 pt-2 mt-[52px]">
                  <Button className="flex-1" onClick={() => handleExport('bs', 'excel')}>
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Excel
                  </Button>
                  <Button className="flex-1" variant="outline" onClick={() => handleExport('bs', 'pdf')}>
                    <FileText className="h-4 w-4 mr-2" />
                    PDF
                  </Button>
                </div>
              </div>
            </Card>

            {/* Trends Export */}
            <Card className="p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5" />
                Financial Trends
              </h3>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Export 12-month financial trend data including revenue, costs, and margins.
                </p>
                <div className="pt-2 mt-[76px]">
                  <Button className="w-full" onClick={() => handleExport('trends', 'excel')}>
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Export to Excel
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* Audit Trail Tab */}
        <TabsContent value="audit" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <History className="h-6 w-6" />
              Audit Trail
            </h2>
            <Button variant="outline" onClick={loadAuditTrail}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Load Audit Log
            </Button>
          </div>

          <Card className="p-4">
            {auditTrail.length > 0 ? (
              <div className="space-y-3">
                {auditTrail.map(entry => (
                  <div key={entry.id} className="p-3 border rounded-lg hover:bg-slate-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge>{entry.action}</Badge>
                          <span className="font-medium">{entry.entity_type}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {entry.user_name || entry.user_email} • {new Date(entry.timestamp).toLocaleString()}
                        </p>
                      </div>
                      <span className="text-xs font-mono text-muted-foreground">{entry.entity_id}</span>
                    </div>
                    {entry.changed_fields?.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Changed: {entry.changed_fields.join(', ')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <History className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Click &quot;Load Audit Log&quot; to view recent activity</p>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* IC Transaction Dialog */}
      <Dialog open={icDialogOpen} onOpenChange={setIcDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5" />
              New Inter-Company Transaction
            </DialogTitle>
            <DialogDescription>
              Create a transaction between two group companies
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>From Company *</Label>
                <Select
                  value={newIcTransaction.from_company_id}
                  onValueChange={(v) => setNewIcTransaction({...newIcTransaction, from_company_id: v})}
                >
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {companies.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>To Company *</Label>
                <Select
                  value={newIcTransaction.to_company_id}
                  onValueChange={(v) => setNewIcTransaction({...newIcTransaction, to_company_id: v})}
                >
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {companies.filter(c => c.id !== newIcTransaction.from_company_id).map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (SAR) *</Label>
                <Input
                  type="number"
                  value={newIcTransaction.amount || ''}
                  onChange={(e) => setNewIcTransaction({...newIcTransaction, amount: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Transaction Type</Label>
                <Select
                  value={newIcTransaction.transaction_type}
                  onValueChange={(v) => setNewIcTransaction({...newIcTransaction, transaction_type: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="service">Service Charge</SelectItem>
                    <SelectItem value="loan">Inter-Company Loan</SelectItem>
                    <SelectItem value="recharge">Cost Recharge</SelectItem>
                    <SelectItem value="dividend">Dividend</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Description *</Label>
              <Input
                value={newIcTransaction.description}
                onChange={(e) => setNewIcTransaction({...newIcTransaction, description: e.target.value})}
                placeholder="Description of the transaction"
              />
            </div>

            <div>
              <Label>Reference</Label>
              <Input
                value={newIcTransaction.reference}
                onChange={(e) => setNewIcTransaction({...newIcTransaction, reference: e.target.value})}
                placeholder="Optional reference number"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIcDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleCreateIcTransaction}
                disabled={!newIcTransaction.from_company_id || !newIcTransaction.to_company_id || !newIcTransaction.amount || !newIcTransaction.description}
              >
                Create Transaction
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Budget Dialog */}
      <Dialog open={budgetDialogOpen} onOpenChange={setBudgetDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Create Budget
            </DialogTitle>
            <DialogDescription>
              Set budget amounts for an account by month
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Fiscal Year *</Label>
                <Input
                  value={newBudget.fiscal_year}
                  onChange={(e) => setNewBudget({...newBudget, fiscal_year: e.target.value})}
                  placeholder="YYYY"
                />
              </div>
              <div>
                <Label>Account *</Label>
                <Select
                  value={newBudget.account_id}
                  onValueChange={(v) => setNewBudget({...newBudget, account_id: v})}
                >
                  <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                  <SelectContent>
                    {accounts.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.account_code} - {a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Budget Type</Label>
                <Select
                  value={newBudget.budget_type}
                  onValueChange={(v) => setNewBudget({...newBudget, budget_type: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="revenue">Revenue</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                    <SelectItem value="capital">Capital</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Monthly Amounts (SAR)</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'].map(month => (
                  <div key={month}>
                    <Label className="text-xs">{month}</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={newBudget.amounts[month] || ''}
                      onChange={(e) => updateBudgetMonth(month, e.target.value)}
                    />
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Total: {formatCurrency(Object.values(newBudget.amounts).reduce((a, b) => a + (b || 0), 0))} SAR
              </p>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={newBudget.notes}
                onChange={(e) => setNewBudget({...newBudget, notes: e.target.value})}
                rows={2}
                placeholder="Optional notes..."
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setBudgetDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleCreateBudget}
                disabled={!newBudget.fiscal_year || !newBudget.account_id}
              >
                Create Budget
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
